
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { PiggyBank, Trophy, Activity } from 'lucide-react';
import { API } from '../utils/api';
import { useAuthContext } from '../context/AuthContext';
import { ModuleGuard } from '../components/ModuleGuard';
import { PersonalEvents } from './parent/PersonalEvents';

export function Tools() {
  const { t } = useTranslation();
  const { user, headers } = useAuthContext();
  const [piggyData, setPiggyData] = useState({ balance: 0, history: [] });
  const [challenges, setChallenges] = useState([]);
  const [newChallenge, setNewChallenge] = useState({ target_steps: 5000, reward_amount: 10, mobpay_id: '', period: 'DAILY' });
  const [boundUsers, setBoundUsers] = useState([]);
  const [syncSteps, setSyncSteps] = useState(0);

  const fetchPiggy = async () => {
    try {
      const res = await axios.get(`${API}/piggy/balance`, { headers });
      setPiggyData(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchChallenges = async () => {
    try {
      const res = await axios.get(`${API}/challenges`, { headers });
      setChallenges(res.data.items || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchBoundUsers = async () => {
    if (user.role === 'parent') {
      const res = await axios.get(`${API}/parent/bound-mobpays`, { headers });
      setBoundUsers(res.data.items || []);
    }
  };

  useEffect(() => {
    fetchPiggy();
    fetchChallenges();
    fetchBoundUsers();
  }, []);

  const createChallenge = async () => {
    try {
      await axios.post(`${API}/challenges/create`, newChallenge, { headers });
      toast.success(t('tools.challenges.challengeCreated'));
      fetchChallenges();
    } catch (e) {
      toast.error(t('tools.challenges.failedToCreate'));
    }
  };

  const handleSync = async (challengeId) => {
    try {
      const res = await axios.post(`${API}/challenges/${challengeId}/sync`, { steps: syncSteps }, { headers });
      if (res.data.completed) {
        toast.success(t('tools.challenges.challengeCompleted'));
        fetchPiggy();
      } else {
        toast.success(t('tools.challenges.stepsSynced'));
      }
      fetchChallenges();
    } catch (e) {
      toast.error(t('tools.challenges.syncFailed'));
    }
  };

  return (
    <div className="space-y-8 pb-20">
      {/* PERSONAL EVENTS SECTION (Personal/payer accounts) - Gated by 'personal_events' module */}
      {(user.role === 'parent' || user.user_type === 'PERSONAL') && (
        <ModuleGuard module="personal_events" featureName={t('tools.personalEvents.featureName')}>
          <PersonalEvents />
        </ModuleGuard>
      )}

      {/* PIGGY BANK SECTION (Only for MobPay) - Gated by 'piggy_bank' module */}
      {user.role === 'mobpay' && (
        <ModuleGuard module="piggy_bank" featureName={t('tools.piggyBank.featureName')} preview={true}>
          <section>
            <div className="flex items-center gap-3 mb-4">
              <PiggyBank className="w-8 h-8 text-pink-500 animate-bounce" />
              <h2 className="text-2xl font-bold text-gray-800">{t('tools.piggyBank.title')}</h2>
            </div>
            
            <Card className="bg-gradient-to-r from-pink-50 to-rose-50 border-pink-200 mb-6">
              <CardContent className="p-6 text-center">
                <div className="text-sm text-pink-600 font-medium mb-1">{t('tools.piggyBank.totalSavings')}</div>
                <div className="text-4xl font-bold text-pink-900">{piggyData.balance} SEK</div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <h3 className="font-semibold text-gray-600">{t('tools.piggyBank.history')}</h3>
              {piggyData.history.length === 0 && <div className="text-sm text-gray-400">{t('tools.piggyBank.noSavings')}</div>}
              {piggyData.history.map(h => (
                <Card key={h.id}>
                  <CardContent className="p-3 flex justify-between items-center">
                    <div>
                      <div className="font-medium text-gray-800">{h.description}</div>
                      <div className="text-xs text-gray-500">{new Date(h.created_at).toLocaleDateString()}</div>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      +{h.amount} SEK
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </ModuleGuard>
      )}

      {/* CHALLENGES SECTION - Gated by 'challenges' module */}
      <ModuleGuard module="challenges" featureName={t('tools.challenges.featureName')} preview={true}>
        <section>
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="w-8 h-8 text-yellow-500 animate-pulse" />
            <h2 className="text-2xl font-bold text-gray-800">{t('tools.challenges.title')}</h2>
          </div>

          {/* Parent: Create Challenge */}
          {user.role === 'parent' && (
            <Card className="mb-6">
              <CardHeader><CardTitle>{t('tools.challenges.newChallenge')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{t('profile.linkedChildren')}</Label>
                  <Select onValueChange={v => setNewChallenge({...newChallenge, mobpay_id: v})}>
                    <SelectTrigger><SelectValue placeholder={t('tools.challenges.selectChild')} /></SelectTrigger>
                    <SelectContent>
                      {boundUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>{t('tools.challenges.targetSteps')}</Label><Input type="number" value={newChallenge.target_steps} onChange={e => setNewChallenge({...newChallenge, target_steps: parseInt(e.target.value)})} /></div>
                  <div><Label>{t('tools.challenges.reward')} (SEK)</Label><Input type="number" value={newChallenge.reward_amount} onChange={e => setNewChallenge({...newChallenge, reward_amount: parseFloat(e.target.value)})} /></div>
                  <div>
                    <Label>{t('tools.challenges.period')}</Label>
                    <Select value={newChallenge.period} onValueChange={v => setNewChallenge({...newChallenge, period: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DAILY">{t('tools.challenges.daily')}</SelectItem>
                        <SelectItem value="WEEKLY">{t('tools.challenges.weekly')}</SelectItem>
                        <SelectItem value="MONTHLY">{t('tools.challenges.monthly')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={createChallenge} className="w-full">{t('tools.challenges.create')}</Button>
              </CardContent>
            </Card>
          )}

          {/* Challenge List */}
          <div className="space-y-4">
            {challenges.map(c => (
              <Card key={c.id} className={c.status === 'COMPLETED' ? 'opacity-75' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity className="w-4 h-4" /> {c.target_steps} {t('tools.challenges.targetSteps')}
                    </CardTitle>
                    <Badge variant={c.status === 'COMPLETED' ? 'secondary' : 'default'}>
                      {c.status === 'COMPLETED' ? t('tools.challenges.completed') : t('tools.challenges.active')}
                    </Badge>
                  </div>
                  <CardDescription>{t('tools.challenges.reward')}: {c.reward_amount} SEK • {t('tools.challenges.period')}: {c.period === 'DAILY' ? t('tools.challenges.daily') : c.period === 'WEEKLY' ? t('tools.challenges.weekly') : t('tools.challenges.monthly')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{t('tools.challenges.progress')}</span>
                      <span>{c.current_steps} / {c.target_steps}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 transition-all duration-500"
                        style={{ width: `${Math.min(100, (c.current_steps / c.target_steps) * 100)}%` }} 
                      />
                    </div>
                    
                    {/* MobPay Action: Sync Steps (Simulation) */}
                    {user.role === 'mobpay' && c.status === 'ACTIVE' && (
                      <div className="flex gap-2 mt-4 pt-4 border-t">
                        <Input 
                          type="number" 
                          placeholder={t('tools.challenges.simulateSteps')} 
                          value={syncSteps} 
                          onChange={e => setSyncSteps(parseInt(e.target.value))} 
                        />
                        <Button onClick={() => handleSync(c.id)}>{t('tools.challenges.sync')}</Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </ModuleGuard>
    </div>
  );
}
